/**
 * /well-testing/rates/new and /well-testing/rates/:id — one rate on
 * the Well Testing rate card. Full pages, no dialogs; the delete is confirmed inline.
 *
 * Deleting a rate leaves every ticket line already priced from it alone (the
 * line keeps its copied rate, code and name) — the confirm says so.
 */
import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useWtCustomers, useWtEquipmentOptions, useWtMeta, useWtPackages } from '../../../hooks/useWellTesting';
import { useCreateWtRate, useDeleteWtRate, useWtEquipmentTypes, useWtRate, useWtRates, useUpdateWtRate } from '../../../hooks/useWtTickets';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { canWriteWtTickets, wtTicketRefusal } from '../../../lib/wt-tickets';
import { catalogLabel, wtDay } from '../../../lib/well-testing';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { isNotFound, WT_EYEBROW, WtEmpty, WtLoading, WtPanel, WtSpec } from '../WtBits';
import { PfInlineConfirm as WtInlineConfirm } from '../../pipeline-facility/digs/DigBits';
import { WtRateForm, type WtRateFormOptions } from './RateForm';
import { wtRateAppliesTo } from './RatesScreen';
import { wtRatePriceText } from './TicketBits';
import type { WtTicketCatalog } from '../../../types/wt-tickets';

function useRateFormOptions(enabled: boolean): WtRateFormOptions {
  const customers = useWtCustomers(enabled).data ?? [];
  const equipment = useWtEquipmentOptions(enabled).data ?? [];
  const equipmentTypes = useWtEquipmentTypes(enabled).data ?? [];
  const packages = (useWtPackages().data?.data ?? []).map((p) => ({ id: p.id, name: p.name || '—' }));
  return { customers, equipment, equipmentTypes, packages };
}

export function WtNewRateScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const back = useBackToList('/well-testing/rates');
  const catalog = (useWtMeta().data?.data ?? null) as WtTicketCatalog | null;
  const metaQuery = useWtRates();
  const options = useRateFormOptions(true);
  const create = useCreateWtRate();
  const [error, setError] = useState<string | null>(null);
  const meta = metaQuery.data?.meta ?? null;
  return (
    <div data-testid="wt-rate-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WT_EYEBROW} title="New rate" description="One line on the rate card." onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px]">
          <WtPanel eyebrow="Rate card" title="New rate">
            <WtRateForm
              catalog={catalog}
              options={options}
              currency={meta?.currency ?? null}
              today={meta?.today ?? null}
              defaultCustomerId={searchParams.get('customerId') ?? ''}
              pending={create.isPending}
              error={error}
              submitLabel="Add rate"
              onCancel={back}
              onSubmit={(body) => {
                setError(null);
                create.mutate(body, {
                  onSuccess: (r) => {
                    toast.success('Rate added');
                    navigate(r.data ? `/well-testing/rates/${r.data.id}` : '/well-testing/rates', { replace: true });
                  },
                  onError: (e) => setError(wtTicketRefusal(e, 'The rate could not be added.')),
                });
              }}
            />
          </WtPanel>
        </div>
      </section>
    </div>
  );
}

export default function WtRateDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/well-testing/rates');
  const { user } = useAuth();
  const canWrite = canWriteWtTickets(user?.role);
  const query = useWtRate(id);
  const catalog = (useWtMeta().data?.data ?? null) as WtTicketCatalog | null;
  const metaQuery = useWtRates(undefined, canWrite);
  const update = useUpdateWtRate();
  const del = useDeleteWtRate();
  const [editing, setEditing] = useState(false);
  const options = useRateFormOptions(canWrite && editing);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const r = query.data?.data ?? null;
  const failure = queryLoadFailure(query);

  return (
    <div data-testid="wt-rate-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title={r?.code || (query.isLoading ? 'Rate' : '—')}
        description={r ? `${r.name} · ${r.customerId ? r.customerName ?? '—' : 'Default card'}` : undefined}
        onBack={back}
        backLabel="Back"
        actions={
          r && canWrite ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setEditError(null); setEditing(true); }} disabled={editing}>
                <Pencil className="size-4" aria-hidden="true" />
                Edit
              </button>
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WtLoading label="Loading rate" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !r) ? (
            <WtPanel eyebrow="Rate card" title="Rate">
              <WtEmpty>This rate was not found. It may have been deleted.</WtEmpty>
            </WtPanel>
          ) : failure || !r ? (
            <WtPanel eyebrow="Rate card" title="Rate">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this rate" />
            </WtPanel>
          ) : (
            <>
              {deleting && (
                <WtInlineConfirm
                  testId="wt-rate-delete-confirm"
                  message={`Delete ${r.code} from the rate card? Ticket lines already priced from it keep their price.`}
                  confirmLabel="Delete rate"
                  cancelLabel="Keep it"
                  pending={del.isPending}
                  error={deleteError}
                  onCancel={() => setDeleting(false)}
                  onConfirm={() =>
                    del.mutate(r.id, {
                      onSuccess: () => {
                        toast.success('Rate deleted');
                        navigate('/well-testing/rates', { replace: true });
                      },
                      onError: (e) => setDeleteError(wtTicketRefusal(e, 'The rate could not be deleted.')),
                    })
                  }
                />
              )}
              {editing && canWrite ? (
                <WtPanel eyebrow="Edit" title={`Edit ${r.code}`}>
                  <WtRateForm
                    rate={r}
                    catalog={catalog}
                    options={options}
                    currency={r.currency}
                    today={metaQuery.data?.meta.today ?? null}
                    pending={update.isPending}
                    error={editError}
                    submitLabel="Save rate"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      update.mutate(
                        { id: r.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Rate saved');
                            setEditing(false);
                          },
                          onError: (e) => setEditError(wtTicketRefusal(e, 'The rate could not be saved.')),
                        },
                      );
                    }}
                  />
                </WtPanel>
              ) : (
                <WtPanel eyebrow="Rate card" title="Details" testId="wt-rate-details">
                  <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                    <WtSpec label="Code" value={r.code || '—'} mono />
                    <WtSpec label="Name" value={r.name || '—'} />
                    <WtSpec label="Card" value={r.customerId ? r.customerName || '—' : 'Default card'} />
                    <WtSpec label="Kind" value={r.kindLabel || catalogLabel(catalog?.priceKinds, r.kind)} />
                    <WtSpec label={r.kind === 'third_party' ? 'Pricing' : 'Rate'} value={wtRatePriceText(r)} mono />
                    <WtSpec label="Unit" value={r.unitLabel || catalogLabel(catalog?.priceUnits, r.unit)} />
                    {r.volumeUnit && <WtSpec label="Volume unit" value={r.volumeUnit} />}
                    <WtSpec label="Currency" value={r.currency || '—'} />
                    <WtSpec label="Applies to" value={wtRateAppliesTo(r, catalog?.crewRoles)} />
                    <WtSpec label="Starts" value={wtDay(r.effectiveFrom)} />
                    <WtSpec label="Ends" value={r.effectiveTo ? wtDay(r.effectiveTo) : 'No end'} />
                  </dl>
                  {(r.description || r.notes) && (
                    <div className="space-y-1 border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                      {r.description && <p>{r.description}</p>}
                      {r.notes && <p className="text-fieldiq-muted">{r.notes}</p>}
                    </div>
                  )}
                </WtPanel>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
